import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Hash, 
  ArrowLeft, 
  Send, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Copy,
  Filter,
  X,
  Upload,
  Image as ImageIcon,
  PlusCircle
} from 'lucide-react';
import { streamOpenAIResponse } from '../../lib/openai';

interface HashtagSet {
  name: string;
  hashtags: string[];
  popularity: 'high' | 'medium' | 'low';
  category: string;
}

const HashtagGenerator = () => {
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [count, setCount] = useState('15');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hashtagSets, setHashtagSets] = useState<HashtagSet[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    
    setImage(file);
    
    // Create image preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    
    // Clear any previous error
    setError(null);
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!topic && !image) {
      setError('Please enter a topic or upload an image');
      return;
    }

    setLoading(true);
    setError(null);
    setHashtagSets([]);

    try {
      let prompt = '';
      
      if (image) {
        // If we have an image, create a prompt that asks for image analysis
        prompt = `Analyze this image and generate ${count} relevant hashtags for a ${platform} post.

The image shows: ${topic || '[Please analyze the image content]'}

Format your response as JSON with the following structure:
[
  {
    "name": "Set name/theme",
    "hashtags": ["#hashtag1", "#hashtag2", "#hashtag3"],
    "popularity": "high/medium/low",
    "category": "category name"
  }
]

Group the hashtags into 3-4 themed sets with different categories and popularity levels.`;
      } else {
        // Standard text-based prompt
        prompt = `Generate ${count} hashtags for a ${platform} post about ${topic}.

Format your response as JSON with the following structure:
[
  {
    "name": "Set name/theme",
    "hashtags": ["#hashtag1", "#hashtag2", "#hashtag3"],
    "popularity": "high/medium/low",
    "category": "category name"
  }
]

Group the hashtags into 3-4 themed sets with different categories and popularity levels.`;
      }

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            // Clean the response text to handle markdown code blocks
            let cleanedResponse = responseText;
            if (cleanedResponse.includes('```json')) {
              cleanedResponse = cleanedResponse.replace(/```json\n|\n```/g, '');
            } else if (cleanedResponse.includes('```')) {
              cleanedResponse = cleanedResponse.replace(/```\n|\n```/g, '');
            }
            
            const parsedData = JSON.parse(cleanedResponse);
            if (Array.isArray(parsedData)) {
              setHashtagSets(parsedData);
              const categories = [...new Set(parsedData.map(set => set.category))];
              setSelectedCategories(categories);
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        }
      );
    } catch (err) {
      setError('Failed to generate hashtags. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (hashtags: string[]) => {
    const text = hashtags.join(' ');
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const filteredHashtagSets = hashtagSets.filter(set => 
    selectedCategories.includes(set.category)
  );

  const getPopularityColor = (popularity: string) => {
    switch (popularity) {
      case 'high':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20';
      case 'low':
        return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center space-x-4">
        <Link to="/ai-tools" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Hashtag Generator</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Find the perfect hashtags to increase your content reach
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Generate Hashtags</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Modern AI Input Bar */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-xl"></div>
            <div className="relative p-1 rounded-xl">
              <div className="flex items-center w-full overflow-hidden bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 transition-all">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={image ? "Optional: Add context about the image" : "e.g., Sunset photography, Vegan recipes, Fitness tips"}
                  className="flex-1 px-4 py-3 bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 dark:text-white"
                />

                <div className="flex items-center pr-2 space-x-1">
                  <button
                    type="submit"
                    disabled={loading || (!topic && !image)}
                    className={`p-2 rounded-md transition-colors ${
                      loading || (!topic && !image)
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                    }`}
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Image Upload Box */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Image Analysis (Optional)
            </label>
            <div className="mt-1">
              {imagePreview ? (
                <div className="relative inline-block">
                  <img 
                    src={imagePreview} 
                    alt="Uploaded preview" 
                    className="h-40 w-auto object-cover rounded-lg border border-gray-300 dark:border-dark-600"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-dark-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                    <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG or GIF (MAX. 5MB)
                    </p>
                  </div>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </label>
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Upload an image to generate hashtags based on visual content analysis
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Platform
              </label>
              <select
                id="platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              >
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="twitter">Twitter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="facebook">Facebook</option>
              </select>
            </div>

            <div>
              <label htmlFor="count" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number of Hashtags
              </label>
              <select
                id="count"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              >
                <option value="10">10 hashtags</option>
                <option value="15">15 hashtags</option>
                <option value="20">20 hashtags</option>
                <option value="30">30 hashtags</option>
              </select>
            </div>
          </div>
        </form>
      </div>

      {hashtagSets.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Hash className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              Generated Hashtags
            </h2>
            
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Filter by category:</span>
            </div>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2">
            {[...new Set(hashtagSets.map(set => set.category))].map(category => (
              <button
                key={category}
                onClick={() => toggleCategory(category)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  selectedCategories.includes(category)
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {selectedCategories.includes(category) ? (
                  <span className="flex items-center">
                    {category}
                    <X className="h-3.5 w-3.5 ml-1.5" />
                  </span>
                ) : (
                  category
                )}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredHashtagSets.map((set, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{set.name}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getPopularityColor(set.popularity)}`}>
                        {set.popularity.charAt(0).toUpperCase() + set.popularity.slice(1)} Popularity
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                        {set.category}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCopy(set.hashtags)}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                    title="Copy hashtags"
                  >
                    {copied === set.hashtags.join(' ') ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {set.hashtags.map((hashtag, i) => (
                    <span 
                      key={i} 
                      className="px-2.5 py-1.5 bg-indigo-50 dark:bg-indigo-900/10 text-indigo-700 dark:text-indigo-300 rounded-lg text-sm"
                    >
                      {hashtag}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default HashtagGenerator;